import {RouterLinkTarget} from '@aboutyou/router';
import {styled} from '@linaria/react';
import React from 'react';
import {Price, PriceV2} from '../../../types/Price';
import {ProductTileSelector} from '../ProductTile/ProductTileSelector';
import {AddToBasketCtaContainer, ProductDetails} from './ProductDetails';
import {ProductTileWrapper} from './ProductTileWrapper';

interface AspectRatio {
  width: number;
  height: number;
}

interface ProductImage {
  src: string;
  description: string;
  isEnclosed: boolean;
  aspectRatio?: AspectRatio;
}

interface FallbackImage {
  url: string;
  description: string;
  aspectRatio?: AspectRatio;
  // / when [true] no CDN features (eg: resize, trim, ...) are expected
  external: boolean;
}

interface VideoSource {
  url: string;
  codecName: string;
}

interface Video {
  description: string;
  fallbackImage: FallbackImage;
  aspectRatio?: AspectRatio;
  sources: VideoSource[];
}

export interface Gallery {
  elements: GalleryElement[];
}

interface GalleryImage {
  image: ProductImage;
}

interface GalleryShopTheLook {
  image: ProductImage;
  label?: string;
}

interface GeneratedImage {
  url: string;
  description: string;
  aspectRatio?: {
    width: number;
    height: number;
  };
  // / when [true] no CDN features (eg: resize, trim, ...) are expected
  external: boolean;
}

interface Gallery_GalleryVideo {
  video: Video;
}

interface GalleryElement {
  type?:
    | {
        $case: 'image';
        image: GalleryImage;
      }
    | {
        $case: 'shopTheLook';
        shopTheLook: GalleryShopTheLook;
      }
    | {
        $case: 'generatedImage';
        generatedImage: GeneratedImage;
      }
    | {
        $case: 'video';
        video: Gallery_GalleryVideo;
      };
}

export interface Color {
  colors: {hexCode: string; isLightColor?: boolean | undefined}[];
  type: 'gradient' | 'simple';
  productId: number;
}

export interface BaseProductTileFeatures {
  importance?: 'high' | 'low' | 'auto';
  productTileGalleryMode?: 'always' | 'hover';
  onColorHover?: (colorProductId: number) => void;
  onColorLeave?: VoidFunction;
  onColorClick?: (colorProductId: number) => void;
  onMouseEnter?: VoidFunction;
  trackGallerySwipe?: VoidFunction;
  enableImageGalleryAnimation?: boolean;
}

export interface BaseProductTileProps extends BaseProductTileFeatures {
  // infos from request
  productId: number;
  to: RouterLinkTarget;
  productName?: string;
  brandName?: string;
  availableSizes?: string[];
  gallery: Gallery;
  price: Price | PriceV2;
  sponsored?: {label: string; onClick: (e: React.MouseEvent) => void};
  colors: Color[];
  // Picture CTA's, badges, prominentBanner goes here
  children?: React.ReactNode;
  // slot which will be rendered at the bottom of the page
  cta?: React.ReactNode;
  tileRef?: React.RefObject<HTMLDivElement | null>;
  gridColumns?: number;
  isSponsoredProductTileUnsampledLoggingEnabled?: boolean;
  enhanceSpeed?: boolean;
  openInNewTab?: boolean;
  onClick?: React.MouseEventHandler<Element>;
}

type Image = {
  alt?: string;
  padded?: boolean;
  src: string;
};

const getGalleryImages = (gallery: Gallery): Image[] => {
  return gallery.elements.map(element => {
    if (element.type?.$case === 'image') {
      return {
        src: element.type.image.image.src,
        alt: element.type.image.image.description,
        padded: element.type.image.image.isEnclosed,
      };
    }
    return {
      src: '',
    };
  });
};

export function getProductTileContentHeight(options: {
  tileWidth?: number;
  totalTileHeight?: number;
}): number {
  if (!options.tileWidth || !options.totalTileHeight) {
    return 0;
  }
  const {tileWidth, totalTileHeight} = options;

  const imageHeight = (4 / 3) * tileWidth;
  return Math.max(0, totalTileHeight - imageHeight);
}

export const BaseProductTile = (props: BaseProductTileProps) => {
  const imageGallery = getGalleryImages(props.gallery);

  return (
    <ProductTileWrapper ref={props.tileRef} onMouseEnter={props.onMouseEnter}>
      <ProductTileSelector
        data-testid={`productTile-${props.productId}`}
        productTileGalleryMode={props.productTileGalleryMode}
        imageGallery={imageGallery}
        primaryImage={imageGallery[0]!}
        to={props.to}
        importance={props.importance}
        trackGallerySwipe={props.trackGallerySwipe}
        enableImageGalleryAnimation={props.enableImageGalleryAnimation}
        columns={props.gridColumns}
        productImageContent={<ImageContent>{props.children}</ImageContent>}
        openInNewTab={props.openInNewTab}
        onClick={props.onClick}
      >
        <ProductDetails
          productName={props.productName}
          brandName={props.brandName}
          colors={props.colors}
          price={props.price}
          onColorHover={props.onColorHover}
          onColorLeave={props.onColorLeave}
          onColorClick={props.onColorClick}
          productId={props.productId}
          sponsored={props.sponsored}
          isSponsoredProductTileUnsampledLoggingEnabled={
            props.isSponsoredProductTileUnsampledLoggingEnabled
          }
        >
          {props.cta && (
            <AddToBasketCtaContainer>{props.cta}</AddToBasketCtaContainer>
          )}
        </ProductDetails>
      </ProductTileSelector>
    </ProductTileWrapper>
  );
};

const ImageContent = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  pointer-events: none;
`;
